import { useRouter } from 'next/router';

import { Box, Heading, List } from '@zillow/constellation-10';

import { useCanAccessCondensedFlow } from '../../../../hooks';
import { useGetListingDetails } from '../../../../hooks/queries/properties';
import propertyUtils from '../../../../utils/propertyUtils';

import { LeaseTermsIntroAndFields } from './components/LeaseTermsIntroAndFields';

import {
  wizardFlexContainerStyles,
  wizardFormSectionContainerStyles,
  wizardHeadingStyles,
} from '../../../../styles/wizard';

import PageWizard from '../../../Wizard/PageWizard';
import WizardTipCardContainer from '../../../WizardTipCardContainer';

import type { WizardBodyTemplateProps } from '../../../WizardStepController';

const LeaseTerms = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  ...rest
}: WizardBodyTemplateProps) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const canAccessCondensedFlow = useCanAccessCondensedFlow();

  const isRoomForRent = propertyUtils.isRoomForRent({ listing: listingDetails });

  const maintenanceDisplay = isRoomForRent
    ? `Outline renter responsibilities for maintenance, if any.`
    : `Outline tenant responsibilities for maintenance, if any.`;

  return (
    <PageWizard
      {...rest}
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      onNextClick={onGoToNextStep || undefined}
      onBackClick={onGoToPrevStep || undefined}
      onExitTriggered={onExitWizard}
    >
      <div data-testid="LeaseTerms">
        <Box css={wizardFlexContainerStyles}>
          <Box css={wizardFormSectionContainerStyles.raw()}>
            <Heading
              level={2}
              textStyle={canAccessCondensedFlow ? 'heading-md' : 'heading-sm'}
              css={wizardHeadingStyles}
            >
              What should renters know about the lease terms?
            </Heading>

            <LeaseTermsIntroAndFields />
          </Box>

          <WizardTipCardContainer header="Tips for lease descriptions" headerLevel={3}>
            <List.Root appearance="bullet">
              <List.Item>Include lease duration.</List.Item>
              <List.Item>
                Add your smoking and parking policies, as well as any other pet policy details not already covered.
              </List.Item>
              <List.Item>{maintenanceDisplay}</List.Item>
            </List.Root>
          </WizardTipCardContainer>
        </Box>
      </div>
    </PageWizard>
  );
};

export { LeaseTermsIntroAndFields } from './components/LeaseTermsIntroAndFields';
export default LeaseTerms;
